import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { QueryStringInput } from '@hello-aria/query-string'
import { useMemo } from 'react'
import { DEFAULT_PAGE_SIZE } from '@/shared/enums/constants'
import type { Company, UpdateCompany } from '@/shared/models/companyModel'
import type { Currency } from '@/shared/models/moneyModel'
import { sortCurrencies } from '@/shared/models/moneyModel'
import { getCompany, listCompanies, updateCompany } from '@/shared/services/companyServices'
import { useMe } from './useMe'
import { deprecated_useSetToast } from './useSetToast'

export const companyQueryKeys = {
    companies: ['companies'],
    company: (id: string): string[] => [...companyQueryKeys.companies, id],
    useInfiniteCompanies: (query?: QueryStringInput): (string | QueryStringInput | undefined)[] => [
        ...companyQueryKeys.companies,
        query
    ]
}

export const useCompany = (): UseQueryResult<Company, Error> => {
    const { companyId } = useMe()

    return useQuery({
        queryKey: companyQueryKeys.company(companyId),
        queryFn: () => getCompany(companyId),
        refetchOnMount: false
    })
}

const DEFAULT_CURRENCIES: Currency[] = ['EUR']
export const useAvailableCurrencies = (company: Company | undefined): Currency[] => {
    return useMemo(() => {
        if (company) {
            const currencies = company.config.currencyConfigs.map(({ currency }) => currency)
            return currencies.length ? sortCurrencies(currencies) : DEFAULT_CURRENCIES
        }

        return []
    }, [company])
}

type UseInfiniteCompanies = {
    query?: QueryStringInput
}

export const useInfiniteCompanies = (options?: UseInfiniteCompanies) => {
    const { query } = options || {}

    return useInfiniteQuery({
        queryKey: companyQueryKeys.useInfiniteCompanies(query),
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            listCompanies({ ...query, pagination: { after: pageParam, first: DEFAULT_PAGE_SIZE } }),
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}

export const useUpdateCompany = (): UseMutationResult<Company, Error, Partial<UpdateCompany>, unknown> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const setToast = deprecated_useSetToast()

    return useMutation({
        mutationFn: (payload) => updateCompany(payload, companyId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: companyQueryKeys.company(companyId) })
            setToast('updateCompany', 'positive')
        }
    })
}
